import React from "react";

import * as S from "./styled";

export interface DealBannerSlot {
  eyebrow: React.ReactNode;
  verdict: React.ReactNode;
  verdictColor?: string;
  meta?: React.ReactNode;
}

export interface DealBannerProps {
  eyebrow?: React.ReactNode;
  verdict?: React.ReactNode;
  verdictColor?: string;
  meta?: React.ReactNode;
  /* Second labelled slot (spec "after leasing enacts a decision"): rendered
     beside the first behind a hairline; both slots become records. */
  secondary?: DealBannerSlot | null;
  /* Right-aligned action cluster (verdict button + kebab, or the existing
     action popover). */
  actions?: React.ReactNode;
  /* Band below the row, inside the card (VERO1 internal-only hold band).
     Content owns its band chrome — the shell only places it. */
  strip?: React.ReactNode;
  /* Band above the row, inside the card (spec 4.4 — the re-screen notice is
     the card's first section, not a separate element stacked on top). */
  topBand?: React.ReactNode;
  /* The factor-cards sheet renders flush below: square the bottom corners so
     banner + sheet read as one composed card. */
  docked?: boolean;
  /* Fallback slot content when there is no verdict (non-VERO1 deals). */
  children?: React.ReactNode;
}

const SlotBody = ({ eyebrow, verdict, verdictColor, meta }: DealBannerSlot) => (
  <>
    {eyebrow != null && <S.Eyebrow>{eyebrow}</S.Eyebrow>}
    {verdict != null && <S.Verdict $color={verdictColor}>{verdict}</S.Verdict>}
    {meta != null && <S.Meta>{meta}</S.Meta>}
  </>
);

export const DealBanner = ({
  eyebrow,
  verdict,
  verdictColor,
  meta,
  secondary,
  actions,
  strip,
  topBand,
  docked,
  children,
}: DealBannerProps) => {
  const hasVerdict = eyebrow != null || verdict != null || meta != null;
  const paired = secondary != null;
  return (
    <S.Banner data-testid="deal-banner" $docked={docked}>
      {topBand}
      <S.Row>
        <S.Slot $paired={paired}>
          {hasVerdict ? (
            <SlotBody
              eyebrow={eyebrow}
              verdict={verdict}
              verdictColor={verdictColor}
              meta={meta}
            />
          ) : (
            children
          )}
        </S.Slot>
        {paired && (
          <>
            <S.SlotDivider />
            <S.Slot $paired>
              <SlotBody {...secondary} />
            </S.Slot>
          </>
        )}
        {actions != null && <S.Actions>{actions}</S.Actions>}
      </S.Row>
      {strip}
    </S.Banner>
  );
};
